/**
 * Relations list page — view and audit user relationships.
 * Filter by type, status; search by name/email; paginated table.
 */

import { useCallback, useEffect, useState } from "react";
import {
  type AdminRelation,
  type RelationStatus,
  type RelationType,
  fetchRelations,
} from "../../api/admin-relations";
import { PageHeader } from "../../components/PageHeader";
import { StatsBar } from "../../components/StatsBar";
import { t } from "../../i18n";
import { listStyles as s } from "../../styles/list-page";

const PAGE_SIZE = 20;

const TYPE_OPTIONS = ["all", "h2h", "h2a"] as const;
const STATUS_OPTIONS = [
  "all",
  "active",
  "deleted",
  "suspended",
  "terminated",
] as const;

export function RelationsPage(): React.JSX.Element {
  const [query, setQuery] = useState("");
  const [type, setType] = useState<string>("all");
  const [status, setStatus] = useState<string>("all");
  const [offset, setOffset] = useState(0);
  const [items, setItems] = useState<AdminRelation[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchRelations({
        q: query || undefined,
        type: type === "all" ? undefined : (type as RelationType),
        status: status === "all" ? undefined : (status as RelationStatus),
        limit: PAGE_SIZE,
        offset,
      });
      setItems(res.items);
      setTotal(res.total);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t("common.failed_to_load"));
    } finally {
      setLoading(false);
    }
  }, [query, type, status, offset]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const resetOffset = useCallback((): void => setOffset(0), []);

  return (
    <div>
      <PageHeader
        title={t("relations.title")}
        description={t("relations.description")}
      />
      <StatsBar items={[{ label: t("stats.total"), value: total }]} />
      <RelationFilters
        query={query}
        type={type}
        status={status}
        onQueryChange={setQuery}
        onTypeChange={(v) => {
          setType(v);
          resetOffset();
        }}
        onStatusChange={(v) => {
          setStatus(v);
          resetOffset();
        }}
        onSearch={resetOffset}
      />
      {error && <p style={s.error}>{error}</p>}
      {loading ? (
        <p style={s.loading}>{t("common.loading")}</p>
      ) : (
        <>
          <RelationTable items={items} />
          <RelationPagination
            offset={offset}
            total={total}
            onChange={setOffset}
          />
        </>
      )}
    </div>
  );
}

// ─── Filters ───

type FilterProps = {
  query: string;
  type: string;
  status: string;
  onQueryChange: (v: string) => void;
  onTypeChange: (v: string) => void;
  onStatusChange: (v: string) => void;
  onSearch: () => void;
};

function RelationFilters(p: FilterProps): React.JSX.Element {
  return (
    <div style={s.filterBar}>
      <input
        type="text"
        placeholder={t("relations.search_placeholder")}
        value={p.query}
        onChange={(e) => p.onQueryChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") p.onSearch();
        }}
        style={s.searchInput}
      />
      <select
        value={p.type}
        onChange={(e) => p.onTypeChange(e.target.value)}
        style={s.select}
        aria-label={t("relations.col_type")}
      >
        {TYPE_OPTIONS.map((v) => (
          <option key={v} value={v}>
            {v === "all" ? t("relations.all_types") : v.toUpperCase()}
          </option>
        ))}
      </select>
      <select
        value={p.status}
        onChange={(e) => p.onStatusChange(e.target.value)}
        style={s.select}
        aria-label={t("relations.col_status")}
      >
        {STATUS_OPTIONS.map((v) => (
          <option key={v} value={v}>
            {v === "all" ? t("relations.all_status") : capitalize(v)}
          </option>
        ))}
      </select>
      <button type="button" onClick={p.onSearch} style={s.searchBtn}>
        {t("common.search")}
      </button>
    </div>
  );
}

// ─── Table ───

function RelationTable(props: {
  items: AdminRelation[];
}): React.JSX.Element {
  if (props.items.length === 0) {
    return <p style={s.empty}>{t("relations.no_relations")}</p>;
  }
  return (
    <div style={s.tableWrap}>
      <table style={s.table}>
        <thead>
          <tr>
            <th style={s.th}>{t("relations.col_type")}</th>
            <th style={s.th}>{t("relations.col_user_a")}</th>
            <th style={s.th}>{t("relations.col_user_b")}</th>
            <th style={s.th}>{t("relations.col_status")}</th>
            <th style={s.th}>{t("relations.col_conversation")}</th>
            <th style={s.th}>{t("relations.col_created")}</th>
          </tr>
        </thead>
        <tbody>
          {props.items.map((r) => (
            <RelationRow key={r.id} relation={r} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RelationRow(props: {
  relation: AdminRelation;
}): React.JSX.Element {
  const { relation: r } = props;
  return (
    <tr>
      <td style={s.td}>{r.type.toUpperCase()}</td>
      <td style={s.td}>
        {r.user_a_name} ({r.user_a_email})
      </td>
      <td style={s.td}>
        {r.user_b_name} ({r.user_b_email})
      </td>
      <td style={s.td}>
        <StatusBadge status={r.status} />
      </td>
      <td style={s.tdMono}>{r.conversation_id.slice(0, 8)}</td>
      <td style={s.td}>{formatDate(r.created_at)}</td>
    </tr>
  );
}

// ─── Pagination ───

function RelationPagination(props: {
  offset: number;
  total: number;
  onChange: (v: number) => void;
}): React.JSX.Element {
  const page = Math.floor(props.offset / PAGE_SIZE) + 1;
  const totalPages = Math.max(1, Math.ceil(props.total / PAGE_SIZE));
  return (
    <div style={s.pagination}>
      <span style={s.pageInfo}>
        {t("relations.page_info", { total: props.total, page, totalPages })}
      </span>
      <div style={s.pageButtons}>
        <button
          type="button"
          disabled={props.offset === 0}
          onClick={() => props.onChange(Math.max(0, props.offset - PAGE_SIZE))}
          style={s.pageBtn}
        >
          {t("common.previous")}
        </button>
        <button
          type="button"
          disabled={props.offset + PAGE_SIZE >= props.total}
          onClick={() => props.onChange(props.offset + PAGE_SIZE)}
          style={s.pageBtn}
        >
          {t("common.next")}
        </button>
      </div>
    </div>
  );
}

// ─── StatusBadge ───

const STATUS_COLOR: Record<RelationStatus, { bg: string; fg: string }> = {
  active: { bg: "#dcfce7", fg: "#166534" },
  deleted: { bg: "#f3f4f6", fg: "#6b7280" },
  suspended: { bg: "#fef9c3", fg: "#854d0e" },
  terminated: { bg: "#fef2f2", fg: "#991b1b" },
};

function StatusBadge(props: {
  status: RelationStatus;
}): React.JSX.Element {
  const c = STATUS_COLOR[props.status];
  return (
    <span style={{ ...s.badge, background: c.bg, color: c.fg }}>
      {capitalize(props.status)}
    </span>
  );
}

// ─── Helpers ───

function capitalize(v: string): string {
  return v.charAt(0).toUpperCase() + v.slice(1);
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}
